import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { TrendingUp, Activity, Heart, Inbox, ArrowRight } from "lucide-react";
import { useWishlist } from "@/lib/wishlist";
import { listOpenBuyOrders } from "@/lib/buy-orders.functions";
import { normalizeTcgLabel } from "@/lib/marketplace";
import { PriceInline } from "@/lib/currency";

function MiniCardArt({ seed, className = "" }: { seed: number; className?: string }) {
  const hue = (seed * 47) % 360;
  return (
    <div
      className={`relative overflow-hidden rounded-md border border-border ${className}`}
      style={{ background: `linear-gradient(135deg, hsl(${hue} 55% 22%), hsl(${(hue + 60) % 360} 50% 14%))` }}
    >
      <div
        className="absolute inset-0 opacity-30"
        style={{ background: `radial-gradient(circle at 30% 25%, hsl(${(hue + 30) % 360} 80% 60%), transparent 55%)` }}
      />
    </div>
  );
}

function seedFromId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/* ---------- Trending Right Now — live open buy orders ---------- */
export function TrendingRightNow() {
  const fetchOpen = useServerFn(listOpenBuyOrders);
  const { data, isLoading } = useQuery({
    queryKey: ["home", "trending"],
    queryFn: () => fetchOpen(),
  });
  const orders = (data?.orders ?? []).slice(0, 6);

  return (
    <section className="panel flex h-full flex-col p-5">
      <div className="mb-4 flex items-end justify-between gap-2">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" /> Trending right now
          </h2>
          <p className="text-[11px] text-muted-foreground">Most recent open buy orders.</p>
        </div>
        <a href="#open-orders" className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          View all <ArrowRight className="h-3 w-3" />
        </a>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center rounded-md border border-dashed border-border bg-background/30 px-4 py-10 text-center text-xs text-muted-foreground">
          Loading…
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center rounded-md border border-dashed border-border bg-background/30 px-4 py-10 text-center">
          <Inbox className="h-8 w-8 text-muted-foreground/60" />
          <p className="mt-3 text-sm text-muted-foreground">No open buy orders yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {orders.map((o: any) => (
            <Link
              key={o.id}
              to="/orders/$id"
              params={{ id: o.id }}
              className="group flex flex-col gap-2 rounded-md border border-border bg-background/40 p-2.5 transition hover:border-primary/50"
            >
              {o.reference_image_url ? (
                <div className="relative aspect-[5/7] w-full overflow-hidden rounded-md border border-border">
                  <img
                    src={o.reference_image_url}
                    alt={o.card_name ?? "Buy order reference"}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-black/60 px-1.5 py-0.5 text-center text-[8px] uppercase tracking-wider text-white/80 backdrop-blur">
                    Buyer reference — not the seller's card
                  </div>
                </div>
              ) : (
                <MiniCardArt seed={seedFromId(o.id)} className="aspect-[5/7] w-full" />
              )}
              <div className="min-w-0">
                <div className="truncate text-xs font-semibold group-hover:text-primary" title={o.card_name}>
                  {o.card_name}
                </div>
                <div className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">{normalizeTcgLabel(o.tcg)}</div>
              </div>
              <div className="flex items-end justify-between border-t border-border pt-1.5">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Target</div>
                  <div className="font-display text-sm font-bold tabular-nums">
                    <PriceInline usd={(o.max_price_cents ?? 0) / 100} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------- Market Pulse — empty state until aggregate data exists ---------- */
export function MarketPulse() {
  return (
    <section className="panel mb-10 p-5">
      <div className="mb-3 flex items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" /> Market pulse
          </h2>
          <p className="text-xs text-muted-foreground">Platform-wide activity trends will appear here once we have enough data.</p>
        </div>
      </div>
      <div className="rounded-md border border-dashed border-border bg-background/30 px-4 py-10 text-center text-sm text-muted-foreground">
        Not enough activity yet to plot a trend.
      </div>
    </section>
  );
}

/* ---------- Watchlist — local wishlist only, no fake feed ---------- */
export function WatchlistPanel() {
  const saved = useWishlist();

  return (
    <section className="panel flex h-full flex-col p-5">
      <div className="mb-3 flex items-end justify-between gap-2">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" /> Watchlist
          </h2>
          <p className="text-[11px] text-muted-foreground">Buy orders you've saved with the heart icon.</p>
        </div>
        <Link to="/wishlist" className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          View all <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      {saved.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center rounded-md border border-dashed border-border bg-background/30 px-4 py-8 text-center">
          <Heart className="h-8 w-8 text-muted-foreground/60" />
          <p className="mt-3 text-sm text-muted-foreground">
            No saved orders yet — heart any buy order to save it here.
          </p>
          <Link
            to="/"
            hash="open-orders"
            className="btn-primary mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-xs"
          >
            Browse Orders <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {saved.slice(0, 8).map((w) => (
            <li key={w.key} className="py-2.5">
              <div className="text-sm font-semibold">{w.cardName}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {w.tcg}{w.set ? ` · ${w.set}` : ""}{w.cardNumber ? ` · #${w.cardNumber}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ---------- Buy Order Status — links to dashboard, no fake counts ---------- */
export function BuyOrderStatusSummary() {
  return (
    <section className="mb-10">
      <div className="mb-3 flex items-end justify-between">
        <h2 className="font-display text-xl font-bold">Your buy order status</h2>
        <Link to="/dashboard" className="text-xs font-semibold text-primary hover:underline">Open dashboard →</Link>
      </div>
      <div className="panel p-6 text-center text-sm text-muted-foreground">
        Sign in and open your dashboard to see real counts for your buy orders, offers received, and pending payouts.
      </div>
    </section>
  );
}
